import { useCallback, useEffect, useRef, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import Autoplay from 'embla-carousel-autoplay'
import Logo from 'assets/img/landing/logo.svg'
import MainImg from 'assets/img/landing/main_img.png'
import IconGoogle from 'assets/img/landing/icon_google.svg'
import IconApple from 'assets/img/landing/icon_apple.svg'
import Img1 from 'assets/img/landing/img1.png'
import Img2 from 'assets/img/landing/img2.png'
import Img3 from 'assets/img/landing/img3.png'
import SvgCall from 'assets/img/landing/call.svg'
import SvgHeart from 'assets/img/landing/heart.svg'
import SvgPin from 'assets/img/landing/pin.svg'
import Phone1 from 'assets/img/landing/phone1.png'
import Phone2 from 'assets/img/landing/phone2.png'
import Phone3 from 'assets/img/landing/phone3.png'
import Phone4 from 'assets/img/landing/phone4.png'
import LeftDefault from 'assets/img/landing/left_default.svg'
import LeftActive from 'assets/img/landing/left_active.svg'
import RightDefault from 'assets/img/landing/right_default.svg'
import RightActive from 'assets/img/landing/right_active.svg'
import Care1 from 'assets/img/landing/care1.svg'
import Care2 from 'assets/img/landing/care2.svg'
import Care3 from 'assets/img/landing/care3.svg'
import LogoFooter from 'assets/img/landing/logo_footer.svg'

import './landing.tailwind.css'

const Landing = () => {
	const autoplay = useRef(
		Autoplay({
			delay: 4200,
			stopOnMouseEnter: true,
			stopOnInteraction: false,
		}),
	)
	const [emblaRef, emblaApi] = useEmblaCarousel(
		{
			align: 'start',
			loop: false,
		},
		[autoplay.current],
	)
	const [canScrollPrev, setCanScrollPrev] = useState(false)
	const [canScrollNext, setCanScrollNext] = useState(false)
	const [pressedButton, setPressedButton] = useState<'prev' | 'next' | null>(
		null,
	)
	const [isHeaderVisible, setIsHeaderVisible] = useState(true)
	const [isHeaderBlurred, setIsHeaderBlurred] = useState(false)

	const onSelect = useCallback(() => {
		if (!emblaApi) {
			return
		}

		setCanScrollPrev(emblaApi.canScrollPrev())
		setCanScrollNext(emblaApi.canScrollNext())
	}, [emblaApi])

	useEffect(() => {
		if (!emblaApi) {
			return
		}

		onSelect()
		emblaApi.on('select', onSelect)
		emblaApi.on('reInit', onSelect)

		return () => {
			emblaApi.off('select', onSelect)
			emblaApi.off('reInit', onSelect)
		}
	}, [emblaApi, onSelect])

	useEffect(() => {
		const revealElements = Array.from(
			document.querySelectorAll<HTMLElement>('[data-reveal]'),
		)

		if (revealElements.length === 0) {
			return
		}

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) {
						return
					}

					entry.target.classList.add('is-visible')
					observer.unobserve(entry.target)
				})
			},
			{
				rootMargin: '0px 0px 8% 0px',
				threshold: 0.08,
			},
		)

		revealElements.forEach((element) => observer.observe(element))

		return () => {
			observer.disconnect()
		}
	}, [])

	useEffect(() => {
		let lastScrollY = window.scrollY

		const handleScroll = () => {
			const currentScrollY = window.scrollY
			const scrollDelta = currentScrollY - lastScrollY

			setIsHeaderBlurred(currentScrollY > 24)

			if (currentScrollY <= 16) {
				setIsHeaderVisible(true)
				lastScrollY = currentScrollY
				return
			}

			if (scrollDelta > 6 && currentScrollY > 120) {
				setIsHeaderVisible(false)
			} else if (scrollDelta < -6) {
				setIsHeaderVisible(true)
			}

			lastScrollY = currentScrollY
		}

		window.addEventListener('scroll', handleScroll, { passive: true })

		return () => {
			window.removeEventListener('scroll', handleScroll)
		}
	}, [])

	const releaseButton = () => {
		setPressedButton(null)
	}

	return (
		<div className="landing-tailwind">
			<header
				className={[
					'landing-header fixed left-0 top-0 z-30 flex w-full items-center justify-center px-4 lg:px-0 py-0 transition-all duration-300',
					isHeaderVisible
						? 'translate-y-0 opacity-100'
						: '-translate-y-full opacity-0',
					isHeaderBlurred ? 'landing-header-blurred' : 'landing-header-resting',
				].join(' ')}
			>
				<div className="container py-3 md:py-6">
					<h1 className="m-0">
						<img
							src={Logo}
							alt="Onjongil"
							className="p-0 m-0"
						/>
					</h1>
				</div>
			</header>

			<div className="relative mb-20 h-full md:mb-24 lg:mb-32">
				<img
					src={MainImg}
					alt="Main"
					className="h-[92vh] min-h-[680px] w-full object-cover object-center sm:h-screen"
				/>
				<div className="absolute left-0 top-[56%] w-full -translate-y-1/2 transform md:top-1/2 px-4 lg:px-0">
					<div
						className="container landing-reveal landing-reveal-hero"
						data-reveal
					>
						<h2 className="m-0 max-w-3xl text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">
							멀리 있어도 마음은 <span className="text-ld-primary-700">‘온종일’</span>
							<br />
							당신과 함께
						</h2>
						<p
							className="my-5 max-w-2xl text-base font-medium leading-7 sm:my-6 sm:text-lg md:max-w-xl lg:my-8 lg:text-2xl lg:leading-10"
							style={{ letterSpacing: '-0.02em' }}
						>
							실시간 모니터링과 AI 안부 전화를 통해
							<br />
							소중한 부모님의 일상을 더 가깝고 안전하게 지켜드립니다.
						</p>
						<ul className="mt-6 mb-0 flex list-none flex-col gap-3 p-0 sm:flex-row sm:gap-4">
							<li>
								<a
									href="https://play.google.com/store/games?hl=ko"
									target="_blank"
									rel="noopener noreferrer"
								>
									<img
										src={IconGoogle}
										alt="Google Play"
										className="h-12 w-auto sm:h-14 lg:h-auto"
									/>
								</a>
							</li>
							<li>
								<a
									href="https://www.apple.com/app-store/"
									target="_blank"
									rel="noopener noreferrer"
								>
									<img
										src={IconApple}
										alt="App Store"
										className="h-12 w-auto sm:h-14 lg:h-auto"
									/>
								</a>
							</li>
						</ul>
					</div>
				</div>
			</div>

			<ol className="container px-4 lg:px-0">
				<li
					className="landing-reveal mb-16 flex flex-col items-center justify-between gap-8 md:mb-24 md:gap-10 lg:mb-32 lg:flex-row"
					data-reveal
					style={{ transitionDelay: '80ms' }}
				>
					<div className="basis-full lg:basis-1/2">
						<img
							src={Img1}
							alt=""
							className="mx-auto w-full object-cover object-center"
						/>
					</div>
					<div className="basis-full lg:basis-1/2">
						<h3 className="mb-0 flex items-center gap-2 text-lg text-ld-primary-500 sm:text-xl lg:text-2xl">
							<img
								src={SvgCall}
								alt="Call"
								className="h-6 w-6 sm:h-7 sm:w-7"
							/>
							AI 안부 전화
						</h3>
						<p className="mb-3 mt-2 text-2xl sm:text-[28px] lg:mb-4 lg:text-3xl">
							따뜻한 목소리로 매일 안부확인
						</p>
						<p className="m-0 text-base leading-7 text-ld-gray-500 sm:text-lg sm:leading-8 lg:text-2xl lg:leading-10">
							전화를 걸어 부모님의 컨디션을 체크하고 기록합니다.
							<br />
							단순한 자동 ARS가 아닌 대화형 AI가 정서적 교감을 나누며, 이상 징후 감지
							시 보호자에게 즉시 알림을 보냅니다.
						</p>
					</div>
				</li>
				<li
					className="landing-reveal mb-16 flex flex-col-reverse items-center justify-between gap-8 md:mb-24 md:gap-10 lg:mb-32 lg:flex-row"
					data-reveal
					style={{ transitionDelay: '140ms' }}
				>
					<div className="basis-full lg:basis-1/2">
						<h3 className="mb-0 flex items-center gap-2 text-lg text-ld-primary-500 sm:text-xl lg:text-2xl">
							<img
								src={SvgHeart}
								alt="Heart"
								className="h-6 w-6 sm:h-7 sm:w-7"
							/>
							실시간 모니터링
						</h3>
						<p className="mb-3 mt-2 text-2xl sm:text-[28px] lg:mb-4 lg:text-3xl">
							언제 어디서든, 건강상태 체크
						</p>
						<p className="m-0 text-base leading-7 text-ld-gray-500 sm:text-lg sm:leading-8 lg:text-2xl lg:leading-10">
							6가지 건강 데이터를 실시간으로 수집하고 분석합니다.
							<br />
							일상 속 건강 변화를 한눈에 확인할 수 있으며, 누적된 데이터를 기반으로
							체계적인 건강 관리를 지원합니다.
						</p>
					</div>
					<div className="basis-full lg:basis-1/2">
						<img
							src={Img2}
							alt=""
							className="mx-auto w-full object-cover object-center"
						/>
					</div>
				</li>
				<li
					className="landing-reveal mb-16 flex flex-col items-center justify-between gap-8 md:mb-24 md:gap-10 lg:mb-32 lg:flex-row"
					data-reveal
					style={{ transitionDelay: '200ms' }}
				>
					<div className="basis-full lg:basis-1/2">
						<img
							src={Img3}
							alt=""
							className="mx-auto w-full object-cover object-center"
						/>
					</div>
					<div className="basis-full lg:basis-1/2">
						<h3 className="mb-0 flex items-center gap-2 text-lg text-ld-primary-500 sm:text-xl lg:text-2xl">
							<img
								src={SvgPin}
								alt="Pin"
								className="h-6 w-6 sm:h-7 sm:w-7"
							/>
							안심 위치 추적
						</h3>
						<p className="mb-3 mt-2 text-2xl sm:text-[28px] lg:mb-4 lg:text-3xl">
							걱정보다 빠른 위치 확인
						</p>
						<p className="m-0 text-base leading-7 text-ld-gray-500 sm:text-lg sm:leading-8 lg:text-2xl lg:leading-10">
							스마트워치와 연동하여 위치 정보를 실시간으로 확인합니다.
							<br />
							현재 위치와 이동 현황을 한눈에 파악할 수 있어, 걱정을 안심으로
							바꿔드립니다.
						</p>
					</div>
				</li>
			</ol>

			<div
				className="container landing-reveal relative pb-20 md:pb-24 px-4 lg:px-0"
				data-reveal
				style={{ transitionDelay: '100ms' }}
			>
				<ol className="relative z-10 mx-auto mb-6 flex list-none items-center justify-center gap-4 p-0 md:absolute md:bottom-0 md:left-0 md:m-0 md:justify-start hidden lg:flex">
					<li>
						<button
							type="button"
							onClick={() => emblaApi?.scrollPrev()}
							onPointerDown={() => setPressedButton('prev')}
							onPointerUp={releaseButton}
							onPointerLeave={releaseButton}
							onPointerCancel={releaseButton}
							onBlur={releaseButton}
							disabled={!canScrollPrev}
							aria-label="Previous slide"
							className="cursor-pointer border-none bg-transparent disabled:cursor-not-allowed disabled:opacity-30"
						>
							<img
								src={LeftActive}
								alt="Left"
								className="w-10 sm:w-12"
							/>
						</button>
					</li>
					<li>
						<button
							type="button"
							onClick={() => emblaApi?.scrollNext()}
							onPointerDown={() => setPressedButton('next')}
							onPointerUp={releaseButton}
							onPointerLeave={releaseButton}
							onPointerCancel={releaseButton}
							onBlur={releaseButton}
							disabled={!canScrollNext}
							aria-label="Next slide"
							className="cursor-pointer border-none bg-transparent disabled:cursor-not-allowed disabled:opacity-30"
						>
							<img
								src={RightActive}
								alt="Right"
								className="w-10 sm:w-12"
							/>
						</button>
					</li>
				</ol>

				<div
					className="slideViewport"
					ref={emblaRef}
				>
					<ul className="slideContainer m-0 list-none p-0">
						<li className="slideItem">
							<div className="flex flex-col items-start justify-between gap-8 md:gap-10 xl:flex-row xl:items-center xl:gap-16">
								<div className="w-full min-w-0 text-left xl:flex-1 xl:pr-8">
									<h3 className="text-3xl leading-tight sm:text-4xl lg:text-5xl">
										안심을 더하는 건강 파트너
									</h3>
									<p
										className="text-base leading-7 text-ld-gray-500 sm:text-lg sm:leading-8 lg:text-xl lg:leading-9"
										style={{ wordBreak: 'keep-all' }}
									>
										건강 상태를 한눈에 파악하고 데이터 흐름을 통해 건강 변화를 더욱 깊이
										있게 들여다보세요.
									</p>
								</div>
								<div className="flex w-full shrink-0 items-end justify-center gap-3 sm:gap-4 xl:w-auto xl:justify-end">
									<img
										src={Phone1}
										alt="Phone 1"
										className="w-36 sm:w-44 lg:w-auto"
									/>
									<img
										src={Phone2}
										alt="Phone 2"
										className="w-36 sm:w-44 lg:w-auto"
									/>
								</div>
							</div>
						</li>
						<li className="slideItem">
							<div className="flex flex-col items-start justify-between gap-8 md:gap-10 xl:flex-row xl:items-center xl:gap-16">
								<div className="w-full min-w-0 text-left xl:flex-1 xl:pr-8">
									<h3 className="text-3xl leading-tight sm:text-4xl lg:text-5xl">
										당신을 위한 따뜻한 기술
									</h3>
									<p
										className="text-base leading-7 text-ld-gray-500 sm:text-lg sm:leading-8 lg:text-xl lg:leading-9"
										style={{ wordBreak: 'keep-all' }}
									>
										바쁜 일상 속, 당신의 목소리에 온전히 귀 기울이는 AI가 감정을 이해하는
										세심한 기술로 대화의 깊이를 더합니다.
									</p>
								</div>
								<div className="flex w-full shrink-0 items-end justify-center gap-3 sm:gap-4 xl:w-auto xl:justify-end">
									<img
										src={Phone3}
										alt="Phone 3"
										className="w-36 sm:w-44 lg:w-auto"
									/>
									<img
										src={Phone4}
										alt="Phone 4"
										className="w-36 sm:w-44 lg:w-auto"
									/>
								</div>
							</div>
						</li>
					</ul>
				</div>
			</div>

			<div className="bg-ld-gray-100 mt-20 py-16 sm:mt-24 sm:py-20 lg:py-24">
				<div className="container px-4 lg:px-0">
					<h3
						className="landing-reveal m-0 text-center text-3xl sm:text-4xl lg:text-5xl"
						data-reveal
					>
						안심과 편안함을 전하는 돌봄 서비스
					</h3>
					<p
						className="landing-reveal my-3 text-center text-base sm:my-4 sm:text-lg lg:text-2xl"
						data-reveal
						style={{ transitionDelay: '80ms' }}
					>
						돌봄의 품격을 높이는 기술, 삶의 가치를 지키는 마음
					</p>
					<ul className="landing-equal-height-list">
						<li
							className="landing-reveal"
							data-reveal
							style={{ transitionDelay: '140ms' }}
						>
							<img
								src={Care1}
								alt="Care 1"
								className="mx-auto h-14 w-14 sm:h-16 sm:w-16"
							/>
							<h4 className="my-2 text-xl sm:text-2xl">쉽게 쓰는 스마트한 기술</h4>
							<p className="m-0 text-sm leading-6 sm:text-base sm:leading-7 lg:text-lg lg:leading-8">
								복잡한 조작 없이도 부모님의 건강과 안전을 24시간 실시간으로 꼼꼼하게
								지킵니다.
							</p>
						</li>
						<li
							className="landing-reveal"
							data-reveal
							style={{ transitionDelay: '200ms' }}
						>
							<img
								src={Care2}
								alt="Care 2"
								className="mx-auto h-14 w-14 sm:h-16 sm:w-16"
							/>
							<h4 className="my-2 text-xl sm:text-2xl">
								마음까지 보살피는 든든한 동반자
							</h4>
							<p className="m-0 text-sm leading-6 sm:text-base sm:leading-7 lg:text-lg lg:leading-8">
								도움이 필요한 모든 순간, 손을 잡아줄 준비된 전문가들과 함께합니다.
							</p>
						</li>
						<li
							className="landing-reveal"
							data-reveal
							style={{ transitionDelay: '260ms' }}
						>
							<img
								src={Care3}
								alt="Care 3"
								className="mx-auto h-14 w-14 sm:h-16 sm:w-16"
							/>
							<h4 className="my-2 text-xl sm:text-2xl">매일이 안심되는 행복한 일상</h4>
							<p className="m-0 text-sm leading-6 sm:text-base sm:leading-7 lg:text-lg lg:leading-8">
								모든 어르신이 소회 없이 안전하고 따뜻한 일상을 누릴 수 있도록
								앞장섭니다.
							</p>
						</li>
					</ul>
				</div>
			</div>

			<footer className="bg-ld-gray-900 py-10 sm:py-12">
				<div className="container px-4 lg:px-0 footer-color flex flex-col gap-8 justify-between lg:flex-row lg:items-end">
					<div
						className="landing-reveal"
						data-reveal
					>
						<img
							src={LogoFooter}
							alt="Onjongil"
							className="mb-4 w-28 sm:w-32 lg:w-auto"
						/>
						<ol className="m-0 flex list-none flex-col gap-y-2 p-0 text-sm leading-6 sm:text-base lg:flex-wrap lg:flex-row lg:gap-x-8 lg:gap-y-1">
							<li>사이트명 : 온종일</li>
							<li>사업자명 : 주식회사 올인어스</li>
							<li>사업자등록번호 : 176-81-03910</li>
							<li>
								주소 : 서울특별시 강남구 봉은사로 317, 2층 2176호(논현동,
								아모제논현빌딩)
							</li>
							<li>대표번호: 02-1111-2222</li>
						</ol>
					</div>
					<div
						className="landing-reveal text-left lg:text-right"
						data-reveal
						style={{ transitionDelay: '120ms' }}
					>
						<ul className="m-0 flex list-none gap-3 p-0 sm:gap-4 lg:justify-end">
							<li>
								<a
									href="https://play.google.com/store/games?hl=ko"
									target="_blank"
									rel="noopener noreferrer"
								>
									<img
										src={IconGoogle}
										alt="Google Play"
										className="h-11 w-auto sm:h-12"
									/>
								</a>
							</li>
							<li>
								<a
									href="https://www.apple.com/app-store/"
									target="_blank"
									rel="noopener noreferrer"
								>
									<img
										src={IconApple}
										alt="App Store"
										className="h-11 w-auto sm:h-12"
									/>
								</a>
							</li>
						</ul>
						<p className="mt-3 text-xs sm:text-sm">
							Copyright © 2026 주식회사 올인어스. All rights reserved.
						</p>
					</div>
				</div>
			</footer>
		</div>
	)
}

export default Landing
